import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Megaphone, Ticket, FileText, Pencil, Trash2, Loader2, X, Copy, Layers } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import SidePanel from '../SidePanel';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import usePotvrzeni from '../ui/usePotvrzeni';
import Dropdown from '../receptura/Dropdown';
import { SECTION_COLOR, SECTION_BG, CERVENA, fmtKc, Nadpis, RadekPanelu, hlavicky } from '../objednavky/ui';
import { FAZE, UROVNE } from './ui';

/**
 * Zákazníci → Kampaně (`zakaznici.kampane`, od 2026-10-12) — PŘÍPRAVA.
 *
 * Tři pohledy v jedné záložce: Kampaně, Slevové kódy, Šablony zpráv.
 * Kampaň se skládá (cílení podle segmentu a úrovně, kanál, text nebo
 * šablona, slevový kód společný / unikátní, kontrolní skupina), počítá se
 * odhad příjemců a ukazuje náhled. Odesílání a uplatnění kódů v pokladně
 * a Objedname přijdou v další etapě — tlačítko Odeslat je proto vypnuté.
 *
 * Kdo smí dostat zprávu: e-mail každý s adresou, kdo se neodhlásil
 * (obchodní podmínky), SMS jen s výslovným souhlasem.
 * Norma §1 + §1f: detail ve sloupci, úpravy v inspektoru nad třetím sloupcem.
 */

const SEKCE = [
    { id: 'kampane', label: 'Kampaně', Icon: Megaphone },
    { id: 'kody', label: 'Slevové kódy', Icon: Ticket },
    { id: 'sablony', label: 'Šablony', Icon: FileText },
];
const POHLEDY = {
    kampane: [{ id: 'vse', label: 'Vše' }, { id: 'koncept', label: 'Koncepty' }, { id: 'pripravena', label: 'Připravené' }, { id: 'automaticke', label: 'Automatické' }],
    kody: [{ id: 'platne', label: 'Platné' }, { id: 'kampane', label: 'Z kampaní' }, { id: 'osobni', label: 'Osobní' }, { id: 'neplatne', label: 'Neplatné' }, { id: 'vse', label: 'Vše' }],
    sablony: [{ id: 'vse', label: 'Vše' }, { id: 'sms', label: 'SMS' }, { id: 'email', label: 'E-mail' }],
};
const STAV_KAMPANE = { koncept: 'Koncept', pripravena: 'Připravená', pozastavena: 'Pozastavená', odeslana: 'Odeslaná' };
const STAV_KODU = { plati: 'Platí', ceka: 'Ještě neplatí', vyprsel: 'Vypršel', vycerpany: 'Vyčerpaný', vypnuty: 'Vypnutý' };
const SPOUSTECE = [
    { value: 'ohrozeny', label: 'Host přešel mezi ohrožené', popis: 'stálý host 45+ dní bez objednávky' },
    { value: 'druha_objednavka', label: 'Druhá objednávka nepřišla', popis: 'nováček 21 dní po první objednávce' },
    { value: 'narozeniny', label: 'Narozeniny', popis: 'kdo má vyplněné datum' },
    { value: 'nedorazil', label: 'Nedorazil na rezervaci', popis: 'rezervace ve stavu nedorazila' },
];
const TYPY_KODU = [
    { value: 'procenta', label: 'Sleva v %' },
    { value: 'castka', label: 'Sleva v Kč' },
    { value: 'polozka', label: 'Položka zdarma' },
];
const KANALY_OBJ = [{ id: 'rozvoz', label: 'Rozvoz' }, { id: 'bistro', label: 'Bistro' }, { id: 'vydej', label: 'Výdej' }];

async function api(url, metoda = 'GET', telo) {
    const r = await fetch(url, { method: metoda, headers: hlavicky(), body: telo ? JSON.stringify(telo) : undefined });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) {
        const prvni = j.errors ? Object.values(j.errors)[0]?.[0] : null;
        throw new Error(prvni || j.message || 'Akce se nepovedla.');
    }
    return j;
}
const datum = (d) => (d ? new Date(d).toLocaleDateString('cs-CZ') : '');
const popisSlevy = (k) => (k.typ === 'procenta' ? `${Number(k.hodnota)} %` : k.typ === 'castka' ? fmtKc(k.hodnota) : `${k.polozka || 'položka'} zdarma`);
const popisCile = (cil) => {
    const c = cil || {};
    const casti = [];
    if (c.faze?.length) casti.push(c.faze.map(f => FAZE[f]?.label || f).join(', '));
    if (c.urovne?.length) casti.push(c.urovne.map(u => UROVNE[u]?.label || u).join(', '));
    if (Number(c.min_utrata_12m) > 0) casti.push(`útrata 12 m od ${fmtKc(c.min_utrata_12m)}`);
    return casti.join(' · ') || 'všichni zákazníci';
};

export default function KampaneModul({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null }) {
    const [sekce, setSekce] = useState('kampane');
    const [pohled, setPohled] = useState('vse');
    const [hledani, setHledani] = useState('');
    const [seznam, setSeznam] = useState(null);
    const [pocty, setPocty] = useState({});
    const [chyba, setChyba] = useState(null);
    const [vybrany, setVybrany] = useState(null);       // id
    const [uprava, setUprava] = useState(null);         // {druh, zaznam|null, hromadne?}
    const [dosah, setDosah] = useState(null);
    const [obnova, setObnova] = useState(0);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    // Inspektor od horní hrany obsahu (norma §1f)
    const obsahRef = useRef(null);
    const [inspektorTop, setInspektorTop] = useState(0);
    const zmer = useCallback(() => { const el = obsahRef.current; if (el) setInspektorTop(Math.max(0, Math.round(el.getBoundingClientRect().top))); }, []);
    useEffect(() => { zmer(); window.addEventListener('resize', zmer); return () => window.removeEventListener('resize', zmer); }, [zmer, sekce, isMobile]);

    const prepniSekci = (s) => { setSekce(s); setPohled(POHLEDY[s][0].id); setVybrany(null); setSeznam(null); setUprava(null); };

    const nacti = useCallback(async () => {
        try {
            if (sekce === 'kampane') {
                const d = await api(`/api/zakaznici/kampane?pohled=${pohled}`);
                setSeznam(d.kampane); setPocty(d.pocty);
            } else if (sekce === 'kody') {
                const d = await api(`/api/zakaznici/kody?pohled=${pohled}${hledani.trim() ? `&q=${encodeURIComponent(hledani.trim())}` : ''}`);
                setSeznam(d.kody); setPocty(d.pocty);
            } else {
                const d = await api(`/api/zakaznici/sablony${pohled !== 'vse' ? `?kanal=${pohled}` : ''}`);
                setSeznam(d.sablony); setPocty({});
            }
            setChyba(null);
        } catch (e) { setChyba(e.message); setSeznam([]); }
    }, [sekce, pohled, hledani]);
    useEffect(() => { const t = setTimeout(nacti, sekce === 'kody' ? 300 : 0); return () => clearTimeout(t); }, [nacti, sekce, obnova]);

    // Dosah kanálů do panelu: kolik lidí smí dostat e-mail / SMS
    useEffect(() => {
        Promise.all(['email', 'sms'].map(k => api('/api/zakaznici/kampane/odhad', 'POST', { kanal: k, cil: {}, kontrolni_pct: 0 }).catch(() => null)))
            .then(([e, s]) => setDosah({ email: e, sms: s }));
    }, [obnova]);

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;
    const q = hledani.trim().toLowerCase();
    const filtrovane = (seznam || []).filter(x => sekce === 'kody' || !q || String(x.nazev).toLowerCase().includes(q));

    const ulozeno = (id) => { setUprava(null); if (id) setVybrany(id); setObnova(n => n + 1); };

    // ── Panel ──
    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column' }}>
            <Nadpis muted={muted}>Koho můžeme oslovit</Nadpis>
            <RadekPanelu popis="E-mailem" hodnota={dosah?.email ? dosah.email.s_kontaktem.toLocaleString('cs-CZ') : '…'} muted={muted} cara={cara} />
            <RadekPanelu popis="SMS (se souhlasem)" hodnota={dosah?.sms ? dosah.sms.s_kontaktem.toLocaleString('cs-CZ') : '…'} cervene={dosah?.sms?.s_kontaktem === 0} muted={muted} cara={cara} />
            <p style={{ margin: '8px 0 0', fontSize: 11.5, color: muted, lineHeight: 1.45 }}>
                E-mail smí každý s adresou, kdo se neodhlásil (obchodní podmínky). SMS jen s výslovným souhlasem — zapisuje se v kartě hosta.
            </p>
            <Nadpis muted={muted} style={{ marginTop: 16 }}>Stav přípravy</Nadpis>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Kampaně, kódy a šablony se zatím jen připravují. Odesílání zpráv a uplatnění kódů v pokladně a Objedname přijde v další etapě.
            </p>
        </div>
    );

    // ── Řádky seznamu ──
    const radek = (x) => {
        const vyb = x.id === vybrany;
        let titulek = x.nazev, popis = '', vpravo = null;
        if (sekce === 'kampane') {
            popis = `${x.kanal === 'sms' ? 'SMS' : 'E-mail'} · ${x.typ === 'automaticka' ? (SPOUSTECE.find(s => s.value === x.spoustec)?.label || 'automatická') : 'jednorázová'} · ${popisCile(x.cil)}`;
            vpravo = <Stitek text={STAV_KAMPANE[x.stav]} zvyraznit={x.stav === 'pripravena'} isDark={isDark} />;
        } else if (sekce === 'kody') {
            titulek = x.kod;
            popis = `${x.nazev} · ${x.sleva}${x.plati_do ? ` · do ${datum(x.plati_do)}` : ''}${x.kampan ? ` · ${x.kampan}` : ''}${x.zakaznik ? ` · ${x.zakaznik}` : ''}`;
            vpravo = <Stitek text={STAV_KODU[x.stav]} zvyraznit={x.stav === 'plati'} isDark={isDark} />;
        } else {
            popis = `${x.kanal === 'sms' ? 'SMS' : 'E-mail'} · ${x.ucel === 'marketing' ? 'marketing' : 'provozní'}${x.aktivni ? '' : ' · vypnutá'} · ${String(x.text).slice(0, 60)}`;
        }
        return (
            <button key={x.id} type="button" onClick={() => setVybrany(x.id)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer',
                    padding: isMobile ? '10px 16px' : '9px 14px', minHeight: isMobile ? 62 : 54, border: 'none', borderBottom: `1px solid ${cara}`,
                    background: vyb ? SECTION_BG : 'transparent', boxShadow: vyb ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
                }}>
                <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ margin: 0, fontSize: isMobile ? 15 : 13.5, fontWeight: 600, color: 'var(--text-primary)', fontFamily: sekce === 'kody' ? 'ui-monospace, monospace' : undefined }}>{titulek}</p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 12.5 : 11.5, color: muted }}>{popis}</p>
                </div>
                {vpravo}
            </button>
        );
    };

    const akce = sekce === 'kampane'
        ? [{ id: 'nova', label: 'Nová kampaň', Icon: Plus, primarni: true, onClick: () => setUprava({ druh: 'kampan', zaznam: null }) }]
        : sekce === 'kody'
            ? [
                { id: 'novy', label: 'Nový kód', Icon: Plus, primarni: true, onClick: () => setUprava({ druh: 'kod', zaznam: null }) },
                { id: 'hromadne', label: 'Hromadně', Icon: Layers, onClick: () => setUprava({ druh: 'kod', zaznam: null, hromadne: true }) },
            ]
            : [{ id: 'nova', label: 'Nová šablona', Icon: Plus, primarni: true, onClick: () => setUprava({ druh: 'sablona', zaznam: null }) }];

    const prepinac = (
        <div className="flex" style={{ gap: 6, padding: isMobile ? '0 16px 10px' : '0 14px 10px', overflowX: 'auto' }}>
            {SEKCE.map(s => (
                <button key={s.id} type="button" onClick={() => prepniSekci(s.id)}
                    className="inline-flex items-center" style={{
                        gap: 6, height: isMobile ? 40 : 32, padding: '0 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
                        color: sekce === s.id ? '#fff' : 'var(--text-secondary)', background: sekce === s.id ? SECTION_COLOR : 'transparent',
                        border: `1px solid ${sekce === s.id ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                    }}>
                    <s.Icon size={14} /> {s.label}
                </button>
            ))}
        </div>
    );

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: sekce === 'kody' ? 'Hledat kód nebo název…' : 'Hledat podle názvu…', placeholderKratky: 'Hledat' }}
                akce={akce}
                pod={prepinac}
                pohledy={!siroky && !isMobile ? POHLEDY[sekce].map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id] })) : null}
                pohledAktivni={pohled}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : POHLEDY[sekce].map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id] ?? null} aktivni={pohled === p.id} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {chyba && <p className="text-sm text-center" style={{ color: CERVENA, padding: '24px 16px' }}>{chyba}</p>}
                {seznam === null && !chyba && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {seznam && !chyba && filtrovane.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {sekce === 'kampane' ? 'Zatím žádná kampaň.' : sekce === 'kody' ? 'Žádné kódy.' : 'Žádné šablony.'}
                    </p>
                )}
                {filtrovane.map(radek)}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detail = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            {sekce === 'kampane' && <DetailKampane key={`k${vybrany}-${obnova}`} id={vybrany} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara} potvrdit={potvrdit}
                onZpet={() => setVybrany(null)} onUpravit={(k) => setUprava({ druh: 'kampan', zaznam: k })} onZmena={() => setObnova(n => n + 1)}
                onSmazano={() => { setVybrany(null); setObnova(n => n + 1); }} rezervaDole={isMobile ? rezervaDole : null} />}
            {sekce === 'kody' && <DetailKodu kod={(seznam || []).find(x => x.id === vybrany)} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara}
                onZpet={() => setVybrany(null)} onUpravit={(k) => setUprava({ druh: 'kod', zaznam: k })} onZmena={() => setObnova(n => n + 1)} />}
            {sekce === 'sablony' && <DetailSablony sablona={(seznam || []).find(x => x.id === vybrany)} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara} potvrdit={potvrdit}
                onZpet={() => setVybrany(null)} onUpravit={(s) => setUprava({ druh: 'sablona', zaznam: s })} onSmazano={() => { setVybrany(null); setObnova(n => n + 1); }} />}
        </div>
    );

    const nadpisUpravy = uprava && ({
        kampan: uprava.zaznam ? 'Upravit kampaň' : 'Nová kampaň',
        kod: uprava.hromadne ? 'Hromadně vygenerovat kódy' : uprava.zaznam ? 'Upravit slevový kód' : 'Nový slevový kód',
        sablona: uprava.zaznam ? 'Upravit šablonu' : 'Nová šablona',
    })[uprava.druh];

    return (
        <div ref={obsahRef} className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybrany} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detail} panel={panel} />
            {uprava && (
                <SidePanel open onClose={() => setUprava(null)} title={nadpisUpravy} isDark={isDark} isMobile={isMobile}
                    width={440} modal={false} accent={SECTION_COLOR} topOffset={inspektorTop}>
                    <div style={{ padding: 16 }}>
                        {uprava.druh === 'kampan' && <FormKampane zaznam={uprava.zaznam} isDark={isDark} isMobile={isMobile} muted={muted} inputStyle={inputStyle} onUlozeno={ulozeno} onZrusit={() => setUprava(null)} />}
                        {uprava.druh === 'kod' && <FormKodu zaznam={uprava.zaznam} hromadne={!!uprava.hromadne} isDark={isDark} isMobile={isMobile} muted={muted} inputStyle={inputStyle} onUlozeno={ulozeno} onZrusit={() => setUprava(null)} />}
                        {uprava.druh === 'sablona' && <FormSablony zaznam={uprava.zaznam} isDark={isDark} isMobile={isMobile} muted={muted} inputStyle={inputStyle} onUlozeno={ulozeno} onZrusit={() => setUprava(null)} />}
                    </div>
                </SidePanel>
            )}
            {potvrzeni}
        </div>
    );
}

// ── Stavební kostky ─────────────────────────────────────────────────

function Stitek({ text, zvyraznit, isDark }) {
    return (
        <span style={{
            fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0,
            color: zvyraznit ? SECTION_COLOR : 'var(--text-secondary)',
            background: zvyraznit ? `${SECTION_COLOR}1A` : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)'),
            border: `1px solid ${zvyraznit ? `${SECTION_COLOR}40` : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}`,
        }}>{text}</span>
    );
}

function HlavickaDetailu({ titulek, podtitul, stitek, isMobile, isDark, muted, onZpet, akce }) {
    return (
        <div style={{ padding: isMobile ? '6px 16px 12px' : '16px 22px 14px', flexShrink: 0 }}>
            {isMobile && <ZpetTlacitko onClick={onZpet} popis="Zpět na seznam" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
            <div className="flex items-start" style={{ gap: 12 }}>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                        <h2 className="truncate" style={{ margin: 0, fontSize: isMobile ? 19 : 18, fontWeight: 700, color: 'var(--text-primary)' }}>{titulek}</h2>
                        {stitek}
                    </div>
                    {podtitul && <p style={{ margin: '3px 0 0', fontSize: 12.5, color: muted }}>{podtitul}</p>}
                </div>
                {!isMobile && (
                    <button type="button" onClick={onZpet} title="Zavřít" aria-label="Zavřít" className="flex items-center justify-center rounded-lg"
                        style={{ width: 34, height: 34, flexShrink: 0, cursor: 'pointer', color: 'var(--text-secondary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                        <X size={16} />
                    </button>
                )}
            </div>
            {akce && <div className="flex flex-wrap" style={{ gap: 8, marginTop: 12 }}>{akce}</div>}
        </div>
    );
}

function Tlacitko({ children, onClick, primarni = false, nebezpecne = false, disabled = false, isDark, isMobile, title }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} title={title}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, height: isMobile ? 44 : 36, padding: '0 14px', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, flex: isMobile ? '1 1 auto' : undefined,
                color: primarni ? '#fff' : nebezpecne ? CERVENA : 'var(--text-primary)', background: primarni ? SECTION_COLOR : 'transparent',
                border: primarni ? 'none' : `1px solid ${nebezpecne ? `${CERVENA}66` : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
            }}>{children}</button>
    );
}

function Sekce({ nadpis, children, cara, muted, isMobile }) {
    return (
        <div style={{ padding: isMobile ? '14px 16px' : '16px 22px', borderTop: `1px solid ${cara}` }}>
            {nadpis && <Nadpis muted={muted}>{nadpis}</Nadpis>}
            {children}
        </div>
    );
}

function Hodnota({ popis, hodnota, muted }) {
    return (
        <div className="flex" style={{ gap: 10, fontSize: 13.5, padding: '4px 0' }}>
            <span style={{ width: 130, flexShrink: 0, color: muted, fontSize: 12.5 }}>{popis}</span>
            <span className="min-w-0" style={{ color: 'var(--text-primary)' }}>{hodnota}</span>
        </div>
    );
}

function Pole({ popis, children, napoveda, muted }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{popis}</span>
            {children}
            {napoveda && <span style={{ fontSize: 11.5, color: muted }}>{napoveda}</span>}
        </label>
    );
}

function Cipy({ moznosti, hodnota = [], onChange, isDark, isMobile }) {
    const prepni = (id) => onChange(hodnota.includes(id) ? hodnota.filter(x => x !== id) : [...hodnota, id]);
    return (
        <div className="flex flex-wrap" style={{ gap: 6 }}>
            {moznosti.map(m => {
                const on = hodnota.includes(m.id);
                return (
                    <button key={m.id} type="button" onClick={() => prepni(m.id)} title={m.popis}
                        style={{
                            height: isMobile ? 38 : 30, padding: '0 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                            color: on ? '#fff' : 'var(--text-secondary)', background: on ? SECTION_COLOR : 'transparent',
                            border: `1px solid ${on ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                        }}>{m.label}</button>
                );
            })}
        </div>
    );
}

function PatickaFormu({ prace, chyba, onUlozit, onZrusit, isDark, isMobile, text = 'Uložit' }) {
    return (
        <div style={{ position: 'sticky', bottom: 0, paddingTop: 12, marginTop: 4, background: 'inherit' }}>
            {chyba && <p style={{ margin: '0 0 8px', fontSize: 12.5, color: CERVENA }}>{chyba}</p>}
            <div className="flex" style={{ gap: 8 }}>
                <Tlacitko onClick={onZrusit} isDark={isDark} isMobile={isMobile}>Zrušit</Tlacitko>
                <Tlacitko onClick={onUlozit} primarni disabled={prace} isDark={isDark} isMobile={isMobile}>
                    {prace ? <Loader2 size={15} className="animate-spin" /> : null} {text}
                </Tlacitko>
            </div>
        </div>
    );
}

const vstup = (inputStyle, isMobile) => ({ ...inputStyle, height: isMobile ? 44 : 36 });
const oblast = (inputStyle) => ({ ...inputStyle, height: 'auto', minHeight: 120, padding: 10, resize: 'vertical', lineHeight: 1.45 });

// ── Kampaně ─────────────────────────────────────────────────────────

function DetailKampane({ id, isDark, isMobile, muted, cara, potvrdit, onZpet, onUpravit, onZmena, onSmazano, rezervaDole }) {
    const [d, setD] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [prace, setPrace] = useState(false);

    useEffect(() => {
        let platne = true;
        api(`/api/zakaznici/kampane/${id}`).then(x => platne && setD(x)).catch(e => platne && setChyba(e.message));
        return () => { platne = false; };
    }, [id]);

    if (chyba) return <p className="text-sm text-center" style={{ color: muted, padding: 40 }}>{chyba}</p>;
    if (!d) return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: muted }}><Loader2 size={24} className="animate-spin" /></div>;

    const k = d.kampan;
    const o = d.odhad;
    const zmenStav = async (stav) => {
        setPrace(true);
        try { setD(await api(`/api/zakaznici/kampane/${k.id}/stav`, 'POST', { stav })); onZmena(); }
        catch (e) { setChyba(e.message); }
        finally { setPrace(false); }
    };
    const smazat = async () => {
        if (!(await potvrdit(`Smazat kampaň ${k.nazev}?`, { potvrdit: 'Smazat' }))) return;
        try { await api(`/api/zakaznici/kampane/${k.id}`, 'DELETE'); onSmazano(); } catch (e) { setChyba(e.message); }
    };
    const kodPopis = k.kod_rezim === 'spolecny' ? (d.kod ? `společný ${d.kod.kod} · ${d.kod.sleva}` : 'společný kód')
        : k.kod_rezim === 'unikatni' ? `unikátní pro každého · ${popisSlevy(k.kod_predpis || {})} · platí ${k.kod_predpis?.platnost_dni || 14} dní`
            : 'bez kódu';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <HlavickaDetailu titulek={k.nazev} stitek={<Stitek text={STAV_KAMPANE[k.stav]} zvyraznit={k.stav === 'pripravena'} isDark={isDark} />}
                podtitul={`${k.kanal === 'sms' ? 'SMS' : 'E-mail'} · ${k.typ === 'automaticka' ? `automaticky: ${SPOUSTECE.find(s => s.value === k.spoustec)?.label || ''}` : 'jednorázová'}`}
                isMobile={isMobile} isDark={isDark} muted={muted} onZpet={onZpet}
                akce={(
                    <>
                        <Tlacitko onClick={() => onUpravit(k)} isDark={isDark} isMobile={isMobile} disabled={k.stav === 'odeslana'}><Pencil size={14} /> Upravit</Tlacitko>
                        {k.stav === 'koncept' && <Tlacitko onClick={() => zmenStav('pripravena')} primarni disabled={prace} isDark={isDark} isMobile={isMobile}>Označit jako připravenou</Tlacitko>}
                        {k.stav === 'pripravena' && <Tlacitko onClick={() => zmenStav('koncept')} disabled={prace} isDark={isDark} isMobile={isMobile}>Vrátit do konceptu</Tlacitko>}
                        {k.stav === 'pripravena' && k.typ === 'automaticka' && <Tlacitko onClick={() => zmenStav('pozastavena')} disabled={prace} isDark={isDark} isMobile={isMobile}>Pozastavit</Tlacitko>}
                        {k.stav === 'pozastavena' && <Tlacitko onClick={() => zmenStav('pripravena')} disabled={prace} isDark={isDark} isMobile={isMobile}>Obnovit</Tlacitko>}
                        <Tlacitko disabled isDark={isDark} isMobile={isMobile} title="Odesílání přijde v další etapě">Odeslat</Tlacitko>
                        {k.stav === 'koncept' && <Tlacitko onClick={smazat} nebezpecne isDark={isDark} isMobile={isMobile}><Trash2 size={14} /> Smazat</Tlacitko>}
                    </>
                )} />
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Sekce nadpis="Komu" cara={cara} muted={muted} isMobile={isMobile}>
                    <Hodnota popis="Cílení" hodnota={popisCile(k.cil)} muted={muted} />
                    <div className="grid" style={{ gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))', gap: '12px 16px', marginTop: 10 }}>
                        <Cislo popis="V cíli" hodnota={o.v_cili} muted={muted} />
                        <Cislo popis={k.kanal === 'sms' ? 'Se souhlasem SMS' : 'S e-mailem'} hodnota={o.s_kontaktem} muted={muted} />
                        <Cislo popis={`Kontrolní ${k.kontrolni_pct} %`} hodnota={o.kontrolni} muted={muted} />
                        <Cislo popis="Dostane zprávu" hodnota={o.odeslat} muted={muted} velke />
                    </div>
                    {o.bez_souhlasu > 0 && (
                        <p style={{ margin: '10px 0 0', fontSize: 12, color: muted }}>
                            {o.bez_souhlasu} lidí v cíli nejde oslovit ({k.kanal === 'sms' ? 'bez souhlasu se SMS' : 'bez e-mailu nebo odhlášení'}).
                        </p>
                    )}
                </Sekce>
                <Sekce nadpis="Slevový kód" cara={cara} muted={muted} isMobile={isMobile}>
                    <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-primary)' }}>{kodPopis}</p>
                </Sekce>
                <Sekce nadpis={`Náhled${d.nahled.pro ? ` · pro ${d.nahled.pro}` : ''}`} cara={cara} muted={muted} isMobile={isMobile}>
                    <div style={{ borderRadius: 12, padding: '12px 14px', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${cara}` }}>
                        {k.kanal === 'email' && d.nahled.predmet && <p style={{ margin: '0 0 8px', fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{d.nahled.predmet}</p>}
                        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{d.nahled.text}</p>
                        {k.kanal === 'email' && <p style={{ margin: '12px 0 0', fontSize: 11.5, color: muted }}>Nechcete dostávat e-maily? Odhlásit se můžete zde.</p>}
                    </div>
                    {k.kanal === 'sms' && <p style={{ margin: '6px 0 0', fontSize: 11.5, color: muted }}>{d.nahled.text.length} znaků{/[^\x20-\x7E\n\r]/.test(d.nahled.text) ? ' · s diakritikou se do SMS vejde 70 znaků' : ''}</p>}
                </Sekce>
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );
}

function Cislo({ popis, hodnota, muted, velke = false }) {
    return (
        <div className="min-w-0">
            <span style={{ display: 'block', fontSize: 11, color: muted }}>{popis}</span>
            <span style={{ display: 'block', fontSize: velke ? 20 : 16, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{Number(hodnota || 0).toLocaleString('cs-CZ')}</span>
        </div>
    );
}

function FormKampane({ zaznam, isDark, isMobile, muted, inputStyle, onUlozeno, onZrusit }) {
    const [f, setF] = useState(() => ({
        nazev: zaznam?.nazev || '',
        kanal: zaznam?.kanal || 'email',
        typ: zaznam?.typ || 'jednorazova',
        spoustec: zaznam?.spoustec || null,
        cil: { faze: zaznam?.cil?.faze || [], urovne: zaznam?.cil?.urovne || [], min_utrata_12m: zaznam?.cil?.min_utrata_12m || '' },
        sablona_id: zaznam?.sablona_id || null,
        predmet: zaznam?.predmet || '',
        text: zaznam?.text || '',
        kod_rezim: zaznam?.kod_rezim || 'zadny',
        kod_id: zaznam?.kod_id || null,
        kod_predpis: { typ: 'procenta', hodnota: 10, platnost_dni: 14, prefix: '', polozka: '', ...(zaznam?.kod_predpis || {}) },
        kontrolni_pct: zaznam?.kontrolni_pct ?? 10,
    }));
    const [sablony, setSablony] = useState([]);
    const [kody, setKody] = useState([]);
    const [odhad, setOdhad] = useState(null);
    const [prace, setPrace] = useState(false);
    const [chyba, setChyba] = useState(null);
    const nastav = (k, v) => setF(x => ({ ...x, [k]: v }));
    const nastavCil = (k, v) => setF(x => ({ ...x, cil: { ...x.cil, [k]: v } }));
    const nastavPredpis = (k, v) => setF(x => ({ ...x, kod_predpis: { ...x.kod_predpis, [k]: v } }));

    useEffect(() => {
        api(`/api/zakaznici/sablony?kanal=${f.kanal}&jen_aktivni=1`).then(d => setSablony(d.sablony)).catch(() => {});
    }, [f.kanal]);
    useEffect(() => { api('/api/zakaznici/kody?pohled=platne').then(d => setKody(d.kody.filter(k => !k.zakaznik_id))).catch(() => {}); }, []);
    useEffect(() => {
        const t = setTimeout(() => {
            api('/api/zakaznici/kampane/odhad', 'POST', { kanal: f.kanal, cil: { ...f.cil, min_utrata_12m: Number(f.cil.min_utrata_12m) || 0 }, kontrolni_pct: Number(f.kontrolni_pct) || 0 })
                .then(setOdhad).catch(() => setOdhad(null));
        }, 400);
        return () => clearTimeout(t);
    }, [f.kanal, f.cil, f.kontrolni_pct]);

    const ulozit = async () => {
        setPrace(true); setChyba(null);
        try {
            const telo = {
                ...f,
                cil: { ...f.cil, min_utrata_12m: Number(f.cil.min_utrata_12m) || 0 },
                kod_predpis: f.kod_rezim === 'unikatni' ? { ...f.kod_predpis, hodnota: Number(f.kod_predpis.hodnota) || 0, platnost_dni: Number(f.kod_predpis.platnost_dni) || 14 } : null,
                kod_id: f.kod_rezim === 'spolecny' ? f.kod_id : null,
                spoustec: f.typ === 'automaticka' ? f.spoustec : null,
            };
            const d = await api(zaznam ? `/api/zakaznici/kampane/${zaznam.id}` : '/api/zakaznici/kampane', zaznam ? 'PUT' : 'POST', telo);
            onUlozeno(d.kampan.id);
        } catch (e) { setChyba(e.message); }
        finally { setPrace(false); }
    };

    const vlozit = (promenna) => nastav('text', `${f.text}${f.text && !f.text.endsWith(' ') ? ' ' : ''}${promenna}`);

    return (
        <div>
            <Pole popis="Název" muted={muted}><input value={f.nazev} onChange={e => nastav('nazev', e.target.value)} maxLength={120} placeholder="Např. Chybíte nám — podzim" style={vstup(inputStyle, isMobile)} /></Pole>
            <Pole popis="Kanál" muted={muted} napoveda={f.kanal === 'sms' ? 'SMS jen lidem s výslovným souhlasem. Psát bez diakritiky (160 znaků místo 70).' : 'E-mail každému s adresou, kdo se neodhlásil. Odkaz na odhlášení se přidá sám.'}>
                <Cipy moznosti={[{ id: 'email', label: 'E-mail' }, { id: 'sms', label: 'SMS' }]} hodnota={[f.kanal]} onChange={(v) => nastav('kanal', v.find(x => x !== f.kanal) || f.kanal)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            <Pole popis="Kdy" muted={muted}>
                <Cipy moznosti={[{ id: 'jednorazova', label: 'Jednorázově' }, { id: 'automaticka', label: 'Automaticky' }]} hodnota={[f.typ]} onChange={(v) => nastav('typ', v.find(x => x !== f.typ) || f.typ)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            {f.typ === 'automaticka' && (
                <Pole popis="Spouštěč" muted={muted}>
                    <Dropdown value={f.spoustec} onChange={(v) => nastav('spoustec', v)} options={SPOUSTECE} placeholder="Vyber, co kampaň spustí" isDark={isDark} accent={SECTION_COLOR} height={isMobile ? 44 : 36} />
                </Pole>
            )}
            <Pole popis="Segmenty" muted={muted} napoveda="Nic nevybráno = všichni.">
                <Cipy moznosti={['fanousek', 'staly', 'vracejici', 'novacek', 'ohrozeny', 'jednorazovy', 'ztraceny'].map(id => ({ id, label: FAZE[id].label, popis: FAZE[id].popis }))}
                    hodnota={f.cil.faze} onChange={(v) => nastavCil('faze', v)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            <Pole popis="Úrovně FofrTýmu" muted={muted}>
                <Cipy moznosti={Object.entries(UROVNE).map(([id, u]) => ({ id, label: u.label, popis: u.popis }))} hodnota={f.cil.urovne} onChange={(v) => nastavCil('urovne', v)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            <Pole popis="Útrata za 12 měsíců od (Kč)" muted={muted}>
                <input type="number" min={0} value={f.cil.min_utrata_12m} onChange={e => nastavCil('min_utrata_12m', e.target.value)} placeholder="0" style={vstup(inputStyle, isMobile)} />
            </Pole>

            <Pole popis="Šablona" muted={muted} napoveda="Šablona předvyplní text; text pak můžeš upravit.">
                <Dropdown value={f.sablona_id} onChange={(v) => { const s = sablony.find(x => x.id === v); setF(x => ({ ...x, sablona_id: v, text: s ? s.text : x.text, predmet: s?.predmet || x.predmet })); }}
                    options={[{ value: null, label: '— vlastní text' }, ...sablony.map(s => ({ value: s.id, label: s.nazev }))]} placeholder="Bez šablony" isDark={isDark} accent={SECTION_COLOR} height={isMobile ? 44 : 36} />
            </Pole>
            {f.kanal === 'email' && (
                <Pole popis="Předmět" muted={muted}><input value={f.predmet} onChange={e => nastav('predmet', e.target.value)} maxLength={200} style={vstup(inputStyle, isMobile)} /></Pole>
            )}
            <Pole popis="Text" muted={muted} napoveda={`${f.text.length} znaků`}>
                <textarea value={f.text} onChange={e => nastav('text', e.target.value)} rows={6} maxLength={5000} style={oblast(inputStyle)} />
                <div className="flex flex-wrap" style={{ gap: 6 }}>
                    {['{jmeno}', '{kod}', '{sleva}', '{plati_do}', '{body}'].map(p => (
                        <button key={p} type="button" onClick={() => vlozit(p)} style={{ height: 28, padding: '0 9px', borderRadius: 6, fontSize: 11.5, fontFamily: 'ui-monospace, monospace', cursor: 'pointer', color: 'var(--text-secondary)', background: 'transparent', border: `1px dashed ${isDark ? 'rgba(255,255,255,0.18)' : 'var(--border-default)'}` }}>{p}</button>
                    ))}
                </div>
            </Pole>

            <Pole popis="Slevový kód" muted={muted}>
                <Cipy moznosti={[{ id: 'zadny', label: 'Bez kódu' }, { id: 'spolecny', label: 'Společný' }, { id: 'unikatni', label: 'Unikátní pro každého' }]}
                    hodnota={[f.kod_rezim]} onChange={(v) => nastav('kod_rezim', v.find(x => x !== f.kod_rezim) || f.kod_rezim)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            {f.kod_rezim === 'spolecny' && (
                <Pole popis="Kód" muted={muted} napoveda="Jeden kód pro všechny — měří se hůř. Nový kód založíš v pohledu Slevové kódy.">
                    <Dropdown value={f.kod_id} onChange={(v) => nastav('kod_id', v)} options={kody.map(k => ({ value: k.id, label: k.kod, popis: `${k.nazev} · ${k.sleva}` }))} placeholder="Vyber platný kód" isDark={isDark} accent={SECTION_COLOR} height={isMobile ? 44 : 36} />
                </Pole>
            )}
            {f.kod_rezim === 'unikatni' && (
                <div style={{ padding: 12, borderRadius: 10, border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`, marginBottom: 14 }}>
                    <p style={{ margin: '0 0 10px', fontSize: 12, color: muted }}>Každý příjemce dostane vlastní jednorázový kód — přesně se pozná, kdo z kampaně přišel. Kódy vzniknou při odeslání.</p>
                    <Pole popis="Sleva" muted={muted}>
                        <Dropdown value={f.kod_predpis.typ} onChange={(v) => nastavPredpis('typ', v)} options={TYPY_KODU} isDark={isDark} accent={SECTION_COLOR} height={isMobile ? 44 : 36} />
                    </Pole>
                    {f.kod_predpis.typ === 'polozka'
                        ? <Pole popis="Co je zdarma" muted={muted}><input value={f.kod_predpis.polozka || ''} onChange={e => nastavPredpis('polozka', e.target.value)} placeholder="Např. dezert" style={vstup(inputStyle, isMobile)} /></Pole>
                        : <Pole popis={f.kod_predpis.typ === 'procenta' ? 'Kolik %' : 'Kolik Kč'} muted={muted}><input type="number" min={0} value={f.kod_predpis.hodnota} onChange={e => nastavPredpis('hodnota', e.target.value)} style={vstup(inputStyle, isMobile)} /></Pole>}
                    <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Pole popis="Platnost (dní)" muted={muted}><input type="number" min={1} max={365} value={f.kod_predpis.platnost_dni} onChange={e => nastavPredpis('platnost_dni', e.target.value)} style={vstup(inputStyle, isMobile)} /></Pole>
                        <Pole popis="Předpona" muted={muted}><input value={f.kod_predpis.prefix || ''} onChange={e => nastavPredpis('prefix', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())} maxLength={10} placeholder="NAPR. PODZIM" style={vstup(inputStyle, isMobile)} /></Pole>
                    </div>
                </div>
            )}
            <Pole popis="Kontrolní skupina (%)" muted={muted} napoveda="Tahle část cíle zprávu nedostane — porovnáním se pozná, jestli kampaň opravdu přivedla objednávky.">
                <input type="number" min={0} max={50} value={f.kontrolni_pct} onChange={e => nastav('kontrolni_pct', e.target.value)} style={vstup(inputStyle, isMobile)} />
            </Pole>

            <div style={{ padding: '10px 12px', borderRadius: 10, background: SECTION_BG, marginBottom: 6, fontSize: 13 }}>
                {odhad ? (
                    <span style={{ color: 'var(--text-primary)' }}>
                        <b>{odhad.odeslat.toLocaleString('cs-CZ')}</b> dostane zprávu · {odhad.v_cili.toLocaleString('cs-CZ')} v cíli · {odhad.s_kontaktem.toLocaleString('cs-CZ')} lze oslovit · {odhad.kontrolni} kontrolní
                    </span>
                ) : <span style={{ color: muted }}>Počítám odhad…</span>}
            </div>
            <PatickaFormu prace={prace} chyba={chyba} onUlozit={ulozit} onZrusit={onZrusit} isDark={isDark} isMobile={isMobile} />
        </div>
    );
}

// ── Slevové kódy ────────────────────────────────────────────────────

function DetailKodu({ kod: k, isDark, isMobile, muted, cara, onZpet, onUpravit, onZmena }) {
    const [prace, setPrace] = useState(false);
    const [zkopirovano, setZkopirovano] = useState(false);
    if (!k) return <p className="text-sm text-center" style={{ color: muted, padding: 40 }}>Kód není v aktuálním pohledu.</p>;
    const prepnout = async () => {
        setPrace(true);
        try { await api(`/api/zakaznici/kody/${k.id}`, 'PUT', { ...k, aktivni: !k.aktivni }); onZmena(); } finally { setPrace(false); }
    };
    const kopirovat = () => { navigator.clipboard?.writeText(k.kod).then(() => { setZkopirovano(true); setTimeout(() => setZkopirovano(false), 1500); }).catch(() => {}); };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <HlavickaDetailu titulek={<span style={{ fontFamily: 'ui-monospace, monospace' }}>{k.kod}</span>} stitek={<Stitek text={STAV_KODU[k.stav]} zvyraznit={k.stav === 'plati'} isDark={isDark} />}
                podtitul={`${k.nazev} · ${k.sleva}`} isMobile={isMobile} isDark={isDark} muted={muted} onZpet={onZpet}
                akce={(
                    <>
                        <Tlacitko onClick={kopirovat} isDark={isDark} isMobile={isMobile}><Copy size={14} /> {zkopirovano ? 'Zkopírováno' : 'Kopírovat'}</Tlacitko>
                        <Tlacitko onClick={() => onUpravit(k)} isDark={isDark} isMobile={isMobile}><Pencil size={14} /> Upravit</Tlacitko>
                        <Tlacitko onClick={prepnout} disabled={prace} nebezpecne={k.aktivni} isDark={isDark} isMobile={isMobile}>{k.aktivni ? 'Vypnout' : 'Zapnout'}</Tlacitko>
                    </>
                )} />
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Sekce cara={cara} muted={muted} isMobile={isMobile}>
                    <Hodnota popis="Sleva" hodnota={k.sleva} muted={muted} />
                    <Hodnota popis="Platí" hodnota={`${k.plati_od ? `od ${datum(k.plati_od)} ` : ''}${k.plati_do ? `do ${datum(k.plati_do)}` : 'bez konce'}`} muted={muted} />
                    <Hodnota popis="Min. objednávka" hodnota={k.min_objednavka ? fmtKc(k.min_objednavka) : 'bez limitu'} muted={muted} />
                    <Hodnota popis="Použití" hodnota={`${k.pouzito} z ${k.max_pouziti ?? '∞'} · nejvýš ${k.max_na_zakaznika}× na hosta`} muted={muted} />
                    <Hodnota popis="Kde" hodnota={k.kanaly?.length ? k.kanaly.map(x => KANALY_OBJ.find(y => y.id === x)?.label || x).join(', ') : 'všude'} muted={muted} />
                    {k.kampan && <Hodnota popis="Kampaň" hodnota={k.kampan} muted={muted} />}
                    {k.zakaznik && <Hodnota popis="Osobní pro" hodnota={k.zakaznik} muted={muted} />}
                    {k.poznamka && <Hodnota popis="Poznámka" hodnota={k.poznamka} muted={muted} />}
                </Sekce>
                <Sekce cara={cara} muted={muted} isMobile={isMobile}>
                    <p style={{ margin: 0, fontSize: 12, color: muted }}>Uplatnění kódu v pokladně a v Objedname přijde v další etapě; počet použití se pak bude plnit sám.</p>
                </Sekce>
            </div>
        </div>
    );
}

function FormKodu({ zaznam, hromadne, isDark, isMobile, muted, inputStyle, onUlozeno, onZrusit }) {
    const [f, setF] = useState(() => ({
        kod: zaznam?.kod || '', nazev: zaznam?.nazev || '', typ: zaznam?.typ || 'procenta', hodnota: zaznam ? Number(zaznam.hodnota) : 10,
        polozka: zaznam?.polozka || '', min_objednavka: zaznam?.min_objednavka ? Number(zaznam.min_objednavka) : '',
        plati_od: zaznam?.plati_od || '', plati_do: zaznam?.plati_do || '', max_pouziti: zaznam?.max_pouziti ?? '',
        max_na_zakaznika: zaznam?.max_na_zakaznika ?? 1, kanaly: zaznam?.kanaly || [], aktivni: zaznam?.aktivni ?? true,
        poznamka: zaznam?.poznamka || '', pocet: 50, prefix: '',
    }));
    const [prace, setPrace] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [vysledek, setVysledek] = useState(null);
    const nastav = (k, v) => setF(x => ({ ...x, [k]: v }));

    const ulozit = async () => {
        setPrace(true); setChyba(null);
        const cislo = (v) => (v === '' || v === null ? null : Number(v));
        const telo = {
            nazev: f.nazev, typ: f.typ, hodnota: f.typ === 'polozka' ? 0 : Number(f.hodnota), polozka: f.typ === 'polozka' ? f.polozka : null,
            min_objednavka: cislo(f.min_objednavka), plati_od: f.plati_od || null, plati_do: f.plati_do || null,
            kanaly: f.kanaly.length ? f.kanaly : null, poznamka: f.poznamka || null,
        };
        try {
            if (hromadne) {
                const d = await api('/api/zakaznici/kody/generovat', 'POST', { ...telo, pocet: Number(f.pocet), prefix: f.prefix });
                setVysledek(d);
            } else {
                const d = await api(zaznam ? `/api/zakaznici/kody/${zaznam.id}` : '/api/zakaznici/kody', zaznam ? 'PUT' : 'POST', {
                    ...telo, kod: f.kod || null, max_pouziti: cislo(f.max_pouziti), max_na_zakaznika: cislo(f.max_na_zakaznika) || 1, aktivni: f.aktivni,
                });
                onUlozeno(d.kod.id);
            }
        } catch (e) { setChyba(e.message); }
        finally { setPrace(false); }
    };

    if (vysledek) {
        return (
            <div>
                <p style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--text-primary)' }}>Vygenerováno <b>{vysledek.vytvoreno}</b> jednorázových kódů.</p>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: muted, fontFamily: 'ui-monospace, monospace' }}>{vysledek.ukazka.join(' · ')}{vysledek.vytvoreno > 5 ? ' …' : ''}</p>
                <Tlacitko primarni onClick={() => onUlozeno(null)} isDark={isDark} isMobile={isMobile}>Hotovo</Tlacitko>
            </div>
        );
    }

    return (
        <div>
            {hromadne ? (
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Pole popis="Počet kódů" muted={muted}><input type="number" min={1} max={2000} value={f.pocet} onChange={e => nastav('pocet', e.target.value)} style={vstup(inputStyle, isMobile)} /></Pole>
                    <Pole popis="Předpona" muted={muted}><input value={f.prefix} onChange={e => nastav('prefix', e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())} maxLength={10} placeholder="LETAK" style={vstup(inputStyle, isMobile)} /></Pole>
                </div>
            ) : (
                <Pole popis="Kód" muted={muted} napoveda="Nech prázdné a vygeneruje se sám (bez znaků, které se pletou: 0/O, 1/I).">
                    <input value={f.kod} onChange={e => nastav('kod', e.target.value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase())} maxLength={32} placeholder="Např. FOFR10" style={{ ...vstup(inputStyle, isMobile), fontFamily: 'ui-monospace, monospace' }} />
                </Pole>
            )}
            <Pole popis="Název" muted={muted}><input value={f.nazev} onChange={e => nastav('nazev', e.target.value)} maxLength={120} placeholder="Např. Leták v tašce — říjen" style={vstup(inputStyle, isMobile)} /></Pole>
            <Pole popis="Sleva" muted={muted}>
                <Dropdown value={f.typ} onChange={(v) => nastav('typ', v)} options={TYPY_KODU} isDark={isDark} accent={SECTION_COLOR} height={isMobile ? 44 : 36} />
            </Pole>
            {f.typ === 'polozka'
                ? <Pole popis="Co je zdarma" muted={muted}><input value={f.polozka} onChange={e => nastav('polozka', e.target.value)} maxLength={120} placeholder="Např. hranolky" style={vstup(inputStyle, isMobile)} /></Pole>
                : <Pole popis={f.typ === 'procenta' ? 'Kolik %' : 'Kolik Kč'} muted={muted}><input type="number" min={0} max={f.typ === 'procenta' ? 100 : undefined} value={f.hodnota} onChange={e => nastav('hodnota', e.target.value)} style={vstup(inputStyle, isMobile)} /></Pole>}
            <Pole popis="Minimální objednávka (Kč)" muted={muted}><input type="number" min={0} value={f.min_objednavka} onChange={e => nastav('min_objednavka', e.target.value)} placeholder="bez limitu" style={vstup(inputStyle, isMobile)} /></Pole>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Pole popis="Platí od" muted={muted}><input type="date" value={f.plati_od} onChange={e => nastav('plati_od', e.target.value)} style={{ ...vstup(inputStyle, isMobile), colorScheme: isDark ? 'dark' : 'light' }} /></Pole>
                <Pole popis="Platí do" muted={muted}><input type="date" value={f.plati_do} onChange={e => nastav('plati_do', e.target.value)} style={{ ...vstup(inputStyle, isMobile), colorScheme: isDark ? 'dark' : 'light' }} /></Pole>
            </div>
            {!hromadne && (
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Pole popis="Použití celkem" muted={muted}><input type="number" min={1} value={f.max_pouziti} onChange={e => nastav('max_pouziti', e.target.value)} placeholder="bez limitu" style={vstup(inputStyle, isMobile)} /></Pole>
                    <Pole popis="Na jednoho hosta" muted={muted}><input type="number" min={1} value={f.max_na_zakaznika} onChange={e => nastav('max_na_zakaznika', e.target.value)} style={vstup(inputStyle, isMobile)} /></Pole>
                </div>
            )}
            <Pole popis="Kde platí" muted={muted} napoveda="Nic nevybráno = všude.">
                <Cipy moznosti={KANALY_OBJ} hodnota={f.kanaly} onChange={(v) => nastav('kanaly', v)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            <Pole popis="Poznámka" muted={muted}><input value={f.poznamka} onChange={e => nastav('poznamka', e.target.value)} maxLength={255} style={vstup(inputStyle, isMobile)} /></Pole>
            <PatickaFormu prace={prace} chyba={chyba} onUlozit={ulozit} onZrusit={onZrusit} isDark={isDark} isMobile={isMobile} text={hromadne ? 'Vygenerovat' : 'Uložit'} />
        </div>
    );
}

// ── Šablony ─────────────────────────────────────────────────────────

function DetailSablony({ sablona: s, isDark, isMobile, muted, cara, potvrdit, onZpet, onUpravit, onSmazano }) {
    const [chyba, setChyba] = useState(null);
    if (!s) return <p className="text-sm text-center" style={{ color: muted, padding: 40 }}>Šablona není v aktuálním pohledu.</p>;
    const smazat = async () => {
        if (!(await potvrdit(`Smazat šablonu ${s.nazev}?`, { potvrdit: 'Smazat' }))) return;
        try { await api(`/api/zakaznici/sablony/${s.id}`, 'DELETE'); onSmazano(); } catch (e) { setChyba(e.message); }
    };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <HlavickaDetailu titulek={s.nazev} stitek={<Stitek text={s.kanal === 'sms' ? 'SMS' : 'E-mail'} isDark={isDark} />}
                podtitul={`${s.ucel === 'marketing' ? 'Marketing — jen komu smíme psát' : 'Provozní — k objednávce, bez souhlasu'}${s.aktivni ? '' : ' · vypnutá'}`}
                isMobile={isMobile} isDark={isDark} muted={muted} onZpet={onZpet}
                akce={(
                    <>
                        <Tlacitko onClick={() => onUpravit(s)} isDark={isDark} isMobile={isMobile}><Pencil size={14} /> Upravit</Tlacitko>
                        <Tlacitko onClick={smazat} nebezpecne isDark={isDark} isMobile={isMobile}><Trash2 size={14} /> Smazat</Tlacitko>
                    </>
                )} />
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Sekce cara={cara} muted={muted} isMobile={isMobile}>
                    {s.predmet && <p style={{ margin: '0 0 8px', fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{s.predmet}</p>}
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{s.text}</p>
                    {chyba && <p style={{ margin: '10px 0 0', fontSize: 12.5, color: CERVENA }}>{chyba}</p>}
                </Sekce>
            </div>
        </div>
    );
}

function FormSablony({ zaznam, isDark, isMobile, muted, inputStyle, onUlozeno, onZrusit }) {
    const [f, setF] = useState(() => ({
        nazev: zaznam?.nazev || '', kanal: zaznam?.kanal || 'sms', ucel: zaznam?.ucel || 'provozni',
        predmet: zaznam?.predmet || '', text: zaznam?.text || '', aktivni: zaznam?.aktivni ?? true,
    }));
    const [prace, setPrace] = useState(false);
    const [chyba, setChyba] = useState(null);
    const nastav = (k, v) => setF(x => ({ ...x, [k]: v }));
    const ulozit = async () => {
        setPrace(true); setChyba(null);
        try {
            const d = await api(zaznam ? `/api/zakaznici/sablony/${zaznam.id}` : '/api/zakaznici/sablony', zaznam ? 'PUT' : 'POST', { ...f, predmet: f.kanal === 'email' ? f.predmet : null });
            onUlozeno(d.sablona.id);
        } catch (e) { setChyba(e.message); }
        finally { setPrace(false); }
    };
    const diakritika = /[^\x20-\x7E\n\r]/.test(f.text);
    return (
        <div>
            <Pole popis="Název" muted={muted}><input value={f.nazev} onChange={e => nastav('nazev', e.target.value)} maxLength={120} style={vstup(inputStyle, isMobile)} /></Pole>
            <Pole popis="Kanál" muted={muted}>
                <Cipy moznosti={[{ id: 'sms', label: 'SMS' }, { id: 'email', label: 'E-mail' }]} hodnota={[f.kanal]} onChange={(v) => nastav('kanal', v.find(x => x !== f.kanal) || f.kanal)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            <Pole popis="Účel" muted={muted} napoveda="Provozní = k objednávce (zpoždění, zpětné volání), bez souhlasu. Marketing = akce a novinky, jen komu smíme psát.">
                <Cipy moznosti={[{ id: 'provozni', label: 'Provozní' }, { id: 'marketing', label: 'Marketing' }]} hodnota={[f.ucel]} onChange={(v) => nastav('ucel', v.find(x => x !== f.ucel) || f.ucel)} isDark={isDark} isMobile={isMobile} />
            </Pole>
            {f.kanal === 'email' && <Pole popis="Předmět" muted={muted}><input value={f.predmet} onChange={e => nastav('predmet', e.target.value)} maxLength={200} style={vstup(inputStyle, isMobile)} /></Pole>}
            <Pole popis="Text" muted={muted} napoveda={f.kanal === 'sms' ? `${f.text.length} znaků${diakritika ? ' · s diakritikou se do jedné SMS vejde 70 znaků' : ' · do jedné SMS 160 znaků'}` : 'Proměnné: {jmeno}, {kod}, {sleva}, {plati_do}, {body}'}>
                <textarea value={f.text} onChange={e => nastav('text', e.target.value)} rows={7} maxLength={5000} style={oblast(inputStyle)} />
            </Pole>
            <Pole popis="Stav" muted={muted}>
                <Cipy moznosti={[{ id: 'zap', label: 'Aktivní' }]} hodnota={f.aktivni ? ['zap'] : []} onChange={(v) => nastav('aktivni', v.includes('zap'))} isDark={isDark} isMobile={isMobile} />
            </Pole>
            <PatickaFormu prace={prace} chyba={chyba} onUlozit={ulozit} onZrusit={onZrusit} isDark={isDark} isMobile={isMobile} />
        </div>
    );
}
